import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { of, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { MAX_MARKET_WATCH_SYMBOLS } from '@app/_helpers';
import { symbolAditionLimit } from '@app/_shared/strings';
import { AddRemoveMarketWatchSymbolRequest, AddRemoveSymbolResponse, CacheResponse, CreateMarketWatchParams, EditMarketWatchResponse, LastClickedSymbol, MarketWatchData, MarketWatchList, MarketWatchOrderResponse, MarketWatchQuote, MarketWatchQuotes, MarketWatchRows, MarketWatchSettings, MarketWatchSymbolResponse, RenameMarketWatchParams, RetrieveMarketWatch, SetCacheRequestParams, SetSymbolCacheRequestInterface } from '../../structs/market-watch.interface';
import { ConfigurationService, NgxToastrService, SingleIndexQuotes } from '@app/_shared';
import { SearchSymbolRequest } from '@app/app.interface';
import { CacheKeys } from '@app/_helpers';
import { MarketIDMap } from '@app/_shared/shared.enums';
import { DefaultmarketWatch } from '../../structs/market-watch.enum';

@Injectable({
    providedIn: 'root'
})
export class MarketWatchService {

    private _cachedMarketWatchData: MarketWatchList = {};
    private _cachedMarketWatchOrder: MarketWatchOrderResponse = {};
    private _cachedSymbolMap = new Map<string, MarketWatchSymbolResponse>();
    private _defaultMarketWatch: number;
    private _defaultMarketWatchRequest: Promise<number>;
    private _lastClickedSymbol: LastClickedSymbol;
    private _marketWatchSettings: MarketWatchSettings;


    private _marketWatchSettingsRequest: Promise<MarketWatchSettings>;

    constructor(
        private _httpClient: HttpClient,
        private configurationService: ConfigurationService,
        private toastr: NgxToastrService,
    ) {
    }

    getMarketWatchData(marketWatchId: number) {

        if (this._cachedMarketWatchData[marketWatchId]
            && this._cachedMarketWatchData[marketWatchId].staticData
        ) {
            return of(this._cachedMarketWatchData[marketWatchId].staticData);
        } else {
            const dataUrl = `${this.configurationService.applicationUrls.marketWatchUrl}${marketWatchId}/`;
            const dataResponse = this._httpClient.get<MarketWatchData>(dataUrl)
                .pipe(
                    tap(data => {
                        this._cachedMarketWatchData[marketWatchId] = {
                            marketWatchName: data.market_watch.market_watch_name,
                        };
                        this._cachedMarketWatchData[marketWatchId].staticData = data;
                    })
                );

            return dataResponse;
        }
    }

    getMarketWatchOrder() {
        if (this._cachedMarketWatchOrder) {
            return of(this._cachedMarketWatchOrder);
        } else {
            const orderUrl = this.configurationService.applicationUrls.getCacheUrl;
            return this._httpClient.get<CacheResponse>(orderUrl, {
                params: {
                    key: CacheKeys.marketWatchOrder
                }
            }).pipe(
                map(response => {
                    if (response && response.data) {
                        const symbolOrder = JSON.parse(response.data);
                        this._cachedMarketWatchOrder = symbolOrder;
                        return this._cachedMarketWatchOrder;
                    } else {
                        return {};
                    }
                })
            );
        }
    }

    cacheMarketWatchOrder(marketWatchId: number) {
        if (this._cachedMarketWatchOrder[marketWatchId]) {
            return this._cachedMarketWatchOrder[marketWatchId];
        }
    }

    getQuotesData(marketWatchId: number) {
        if (this._cachedMarketWatchData[marketWatchId] && this._cachedMarketWatchData[marketWatchId].quotesData) {
            return of(this._cachedMarketWatchData[marketWatchId].quotesData);
        } else {
            const url = `${this.configurationService.applicationUrls.marketWatchUrl}${marketWatchId}/quotes/`;
            return this._httpClient.get<MarketWatchQuotes>(url)
                .pipe(
                    tap(data => {
                        this._cachedMarketWatchData[marketWatchId].quotesData = data;
                    })
                );
        }
    }

    updateQuoteCache(quote: MarketWatchQuote | SingleIndexQuotes, marketWatchId: number) {
        if (quote?.symbol_id && this._cachedMarketWatchData[marketWatchId] &&
            this._cachedMarketWatchData[marketWatchId].quotesData) {
            this._cachedMarketWatchData[marketWatchId].quotesData[quote.symbol_id] = quote;
        }
    }
    
    getMarketWatchSettings(): Promise<MarketWatchSettings> {
        if (this._marketWatchSettings === undefined) {
            if (this._marketWatchSettingsRequest) {
                return this._marketWatchSettingsRequest;
            } else {
                const url = this.configurationService.applicationUrls.getCacheUrl;
                this._marketWatchSettingsRequest = new Promise((resolve, reject) => {
                    this._httpClient.get<CacheResponse>(url, {
                        params: {
                            key: CacheKeys.marketWatchSettings
                        }
                    }).subscribe(res => {
                        if (res.data) {
                            this._marketWatchSettings = JSON.parse(res.data);
                        } else {
                            this._marketWatchSettings = null;
                        }
                        resolve(this._marketWatchSettings);
                    }, (err) => {
                        reject(err);
                    });
                });
                return this._marketWatchSettingsRequest;
            }
        } else {
            return new Promise(resolve => resolve(this._marketWatchSettings));
        }
    }

    addRemoveSymbol(addMarketWatchSymbolRequest: AddRemoveMarketWatchSymbolRequest, marketWatchId: number, market_id?: number, security_id?: number) {
        const toAdd = addMarketWatchSymbolRequest.add_symbols;
        if (toAdd?.length) {
            const symbols = this._cachedMarketWatchData[marketWatchId]?.staticData?.symbols ?? [];
            if (symbols.length >= MAX_MARKET_WATCH_SYMBOLS) {
                this.toastr.simpleCustomToaster(symbolAditionLimit(MAX_MARKET_WATCH_SYMBOLS), false);
                return;
            }
            for (const sym of toAdd) {
                if (symbols.some(row => parseInt(String(row.market_id), 10) === sym.market_id && row.security_id === sym.security_id)) {
                    this.toastr.simpleCustomToaster('Symbol already exists in market watch', false);
                    return;
                }
            }
        }

        const url = `${this.configurationService.applicationUrls.marketWatchUpdate}${marketWatchId}/`;
        return this._httpClient.put<any>(url, addMarketWatchSymbolRequest)
            .pipe(
                map(data => {
                    if (data.added_symbols && data.added_symbols.length) {
                        if (this._cachedMarketWatchData[marketWatchId]) {
                        this._cachedMarketWatchData[marketWatchId].staticData.symbols.push(data.added_symbols[0]);
                        }
                    } else {
                        const cachedData = this._cachedMarketWatchData[marketWatchId];
                        this._cachedMarketWatchData[marketWatchId].staticData.symbols.forEach((item, index) => {
                            if (item.symbol && item.market_id === market_id && item.security_id === security_id) {
                                this._cachedMarketWatchData[marketWatchId].staticData.symbols.splice(index, 1);
                            }
                        });
                        this._cachedMarketWatchData[marketWatchId] = cachedData;
                    }

                        return data;
                    })
            );
    }

    createMarketWatch(createMarketWatchParams: CreateMarketWatchParams) {
        const url = this.configurationService.applicationUrls.marketWatchUrl;
        return this._httpClient.post<AddRemoveSymbolResponse>(url, createMarketWatchParams);
    }

    listMarketWatch(refresh: boolean = true) {
        if (!refresh && Object.keys(this._cachedMarketWatchData).length) {
            return of(Object.values(this._cachedMarketWatchData).map(data => data.staticData));
        }

        const url = this.configurationService.applicationUrls.marketWatchUrl;

        return this._httpClient.get<MarketWatchData[]>(url)
            .pipe(
                map(data => {
                    data.forEach(row => {
                        // Market watch list now contains all the symbols of that MW. Caching those results
                        // so that no requests are made to web-server for each request (prevents loading screen)
                        this._cachedMarketWatchData[row.market_watch?.id] = {
                            marketWatchName: row.market_watch?.market_watch_name,
                        };
                        this._cachedMarketWatchData[row.market_watch?.id].staticData = row;
                        this.setMarketWatchSymbolsCache(row.symbols);
                    })

                    return data;
                })
            );
    }

    setMarketWatchSymbolsCache(marketWatchRows: MarketWatchSymbolResponse[]) {
        marketWatchRows.forEach(row => {
            const symbol = row;
            const newSymbolKey = this.getSymbolKey(symbol.security_id, symbol.market_id);
            
            this._cachedSymbolMap.set(newSymbolKey, symbol);
        });
    }

    deleteMarketWatch(marketWatchId: number) {
        const url = `${this.configurationService.applicationUrls.marketWatchUrl}${marketWatchId}/`;
        return this._httpClient.delete<EditMarketWatchResponse>(url);
    }

    renameMarketWatch(marketWatchId: number, renameMarketWatchParams: RenameMarketWatchParams) {
        const url = `${this.configurationService.applicationUrls.marketWatchUrl}${marketWatchId}/`;
        return this._httpClient.put<EditMarketWatchResponse>(url, renameMarketWatchParams);
    }

    setMarketWatchOrder(setMarketWatchOrder: SetCacheRequestParams) {
        const url = this.configurationService.applicationUrls.setCacheUrl;
        this._cachedMarketWatchOrder = JSON.parse(setMarketWatchOrder.data);
        console.log("setMarketWatchOrder", this._cachedMarketWatchOrder);
        return this._httpClient.post<any>(url, setMarketWatchOrder);
    }

    getSymbolKey(security_id: number, market_id: number) {
        return market_id + "_" + security_id;
    }

    getMWStaticData(searchSymbolRequest: SearchSymbolRequest) {
        if (searchSymbolRequest.security_id && searchSymbolRequest.market_id) {
            const symbolKey = this.getSymbolKey(+searchSymbolRequest.security_id, parseInt(searchSymbolRequest.market_id));
            if (this._cachedSymbolMap.has(symbolKey)) {
                return of(this._cachedSymbolMap.get(symbolKey));
            }
            return this._httpClient.get<any>(`${this.configurationService.applicationUrls.singleSymbolData}`, {
                params: <any>searchSymbolRequest
            }).pipe(
                tap(response => {
                    const symbolKey = this.getSymbolKey(+searchSymbolRequest.security_id, parseInt(searchSymbolRequest.market_id));
                    this._cachedSymbolMap.set(symbolKey, response);
                }),
                map(response => {
                    return response;
                })
            );
        } else {
            return of(null);
        }
    }


    getDefaultMarketWatch(): Promise<number> {
        if (this._defaultMarketWatch === undefined) {
            if (this._defaultMarketWatchRequest) {
                return this._defaultMarketWatchRequest;
            } else {
                const url = this.configurationService.applicationUrls.getCacheUrl;
                this._defaultMarketWatchRequest = new Promise((resolve, reject) => {
                    this._httpClient.get<CacheResponse>(url, {
                        params: {
                            key: CacheKeys.defaultMarketWatch
                        }
                    }).subscribe(res => {
                        if (res.data) {
                            this._defaultMarketWatch = JSON.parse(res.data);
                        } else {
                            this._defaultMarketWatch = null;
                        }
                        resolve(this._defaultMarketWatch);
                    }, (err) => {
                        reject(err);
                    });
                });
                return this._defaultMarketWatchRequest;
            }
        } else {
            return new Promise(resolve => resolve(this._defaultMarketWatch));
        }
    }

    setDefaultMarketWatch(id: number) {
        const url = this.configurationService.applicationUrls.setCacheUrl;
        const request: SetCacheRequestParams = {
            key: CacheKeys.defaultMarketWatch,
            data: JSON.stringify(id)
        };
        return this._httpClient.post<any>(url, request);
    }

    setLastClickedSymbol(symbol: SetSymbolCacheRequestInterface) {
        const url = this.configurationService.applicationUrls.setCacheUrl;
        this._lastClickedSymbol = symbol.data;
        return this._httpClient.post(url, {
            key: CacheKeys.lastClickedSymbol,
            data: JSON.stringify(symbol.data)
        });
    }

    setMarketWatchSettings(settings: MarketWatchSettings) {
        const url = this.configurationService.applicationUrls.setCacheUrl;
        this._marketWatchSettings = settings;
        const request: SetCacheRequestParams = {
            key: CacheKeys.marketWatchSettings,
            data: JSON.stringify(settings)
        };
        return this._httpClient.post<any>(url, request);
    }
}
